import { useCallback, useEffect, useState } from 'react';
import { apiPostAsync } from '../common/service-utils';
import * as constants from '../common/constants';
import * as errorCodes from '../common/errorCodes';
import * as JspUtil from '../common/JspUtil';
import * as actions from './actionCreators';
import { Paper, Slide } from '@mui/material';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import { useAppContext, useAuth, useJspVariables, useLoader } from '../hooks';
import { DialogTitleWithClose, PageHeader, TextInput } from '../components';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

export const STEP_DEVICE_NAME = 'DEVICE_NAME';
export const STEP_QR_CODE = 'QR_CODE';
export const STEP_SUCCESS = 'SUCCESS';

const SetupAuthenticator = ({
    open,
    onClose,
    onSetAsDefault,
    onDeviceRegistered,
    showHowToGoogleAuthenticatorDialog,
}) => {
    const { jspVariables } = useJspVariables();
    const { getLabelFromJspVariables } = useAppContext(jspVariables);
    const { authenticatedUser } = useAuth();
    const { showLoader, hideLoader } = useLoader();

    const [step, setStep] = useState(STEP_DEVICE_NAME);
    const [deviceName, setDeviceName] = useState('');
    const [txtOtpCodeVerify, setTxtOtpCodeVerify] = useState('');
    const [qrCodeImgSrc, setQrCodeImgSrc] = useState('');
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});

    // Reset on close so reopening starts fresh
    useEffect(() => {
        if (!open) {
            setStep(STEP_DEVICE_NAME);
            setDeviceName('');
            setTxtOtpCodeVerify('');
            setQrCodeImgSrc('');
            setError('');
            setFieldErrors({});
        }
    }, [open]);

    const clearFieldError = useCallback((field) => {
        setFieldErrors(prev => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    }, []);

    const onDeviceNameChange = useCallback((event) => {
        setDeviceName(event.target.value);
        clearFieldError('deviceName');
    }, [clearFieldError]);

    const onOtpCodeChange = useCallback((event) => {
        setTxtOtpCodeVerify(event.target.value);
        clearFieldError('otpCode');
    }, [clearFieldError]);

    const startRegistration = useCallback(async () => {
        if (!deviceName || deviceName.trim() === '') {
            setFieldErrors({ deviceName: 'Device name is required' });
            return;
        }
        setFieldErrors({});
        setError('');
        showLoader('Generating QR code...');
        try {
            const resp = await apiPostAsync(constants.API_GET_TFA_QR_CODE_AUTHENTICATED, null, {});
            setQrCodeImgSrc(resp.data.qrCode || '');
            setStep(STEP_QR_CODE);
        } catch (resp) {
            setError(resp.message || 'Failed to generate QR code.');
        } finally {
            hideLoader();
        }
    }, [deviceName, showLoader, hideLoader]);

    const verifyOtp = useCallback(async () => {
        const errors = {};
        if (!deviceName || deviceName.trim() === '') {
            errors.deviceName = 'Device name is required';
        }
        if (!txtOtpCodeVerify || txtOtpCodeVerify.trim() === '') {
            errors.otpCode = 'OTP is required';
        } else if (!/^\d{6}$/.test(txtOtpCodeVerify.trim())) {
            errors.otpCode = 'OTP must be 6 digits';
        }
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setError('');
        showLoader('Verifying code...');
        try {
            await apiPostAsync(constants.API_VERIFY_TFA_QR_CODE_AUTHENTICATED, null, {
                otpCode: txtOtpCodeVerify,
                deviceName,
            });
            setStep(STEP_SUCCESS);
            if (onDeviceRegistered) {
                try {
                    const devResp = await actions.loadAuthenticatorDevicesAsync(
                        authenticatedUser.authenticatedUser,
                        authenticatedUser.authenticatedApp,
                    );
                    onDeviceRegistered(devResp.data || []);
                } catch (_) { /* ignore */ }
            }
        } catch (resp) {
            const message = Number(resp.code) === errorCodes.ERR_TFA_OTP_ALREADY_USED
                ? 'This OTP has already been used. Please wait for a new code.'
                : (resp.message || 'Verification failed.');
            setError(message);
        } finally {
            hideLoader();
        }
    }, [deviceName, txtOtpCodeVerify, authenticatedUser, onDeviceRegistered, showLoader, hideLoader]);

    const registerAnother = useCallback(() => {
        setDeviceName('');
        setTxtOtpCodeVerify('');
        setQrCodeImgSrc('');
        setFieldErrors({});
        setError('');
        setStep(STEP_DEVICE_NAME);
    }, []);

    return (
        <MuiDialog open={open} onClose={onClose} maxWidth="md" fullWidth
            aria-labelledby="tfa-setup-authenticator-title"
            aria-describedby="tfa-setup-authenticator-description">
            <DialogTitleWithClose id="tfa-setup-authenticator-title" onClose={onClose} dataAutomationId="tfa_setup_authenticator_title">
                {getLabelFromJspVariables('title_setup_authenticator')}
            </DialogTitleWithClose>
            <DialogContent id="tfa-setup-authenticator-description" sx={{ overflow: 'auto', padding: '17px' }}>
                <Box id="tfaQrDiv" data-automation-id="tfa_qr_div" sx={{ position: 'relative', px: '1.125rem' }}>
                    <Box style={{ marginLeft: '0.9rem', marginRight: '0.1rem' }}>
                        <PageHeader dataAutomationId="setup_authenticator_header">{getLabelFromJspVariables('title_register_authenticator')}</PageHeader>

                        {error && <Alert id="setupAuthenticatorAlert" data-automation-id="setup_authenticator_alert" severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}

                        {step === STEP_DEVICE_NAME &&
                            <Box>
                                <TextInput
                                    id="authenticator_device_name"
                                    name="deviceName"
                                    label={getLabelFromJspVariables('label_authenticator_name')}
                                    required
                                    helperText={fieldErrors.deviceName || "Used to identify device if multiple devices registered"}
                                    error={!!fieldErrors.deviceName}
                                    autoComplete="off"
                                    value={deviceName}
                                    onChange={onDeviceNameChange}
                                    sx={{ width: '50%' }}
                                />
                            </Box>
                        }

                        {step === STEP_SUCCESS &&
                            <Alert severity="success" aria-live="polite" sx={{ mb: 2 }}>
                                {getLabelFromJspVariables('status_authenticator_registered')}
                            </Alert>
                        }
                    </Box>

                    {step === STEP_QR_CODE && qrCodeImgSrc && JspUtil.isValueAvailable(qrCodeImgSrc) &&
                        <Box style={{ paddingBottom: '1em' }}>
                            <Slide in={true}>
                                <Paper elevation={0} style={{ marginLeft: '0.9rem', marginRight: '0.1rem' }}>
                                    <Box style={{ marginLeft: '0.3rem', marginRight: '1rem' }}>
                                        {`${getLabelFromJspVariables('authenticator_information')}
                                        ${getLabelFromJspVariables('note_mobile_more_info')}
                                        please `}
                                        <Link component="button" type="button" id="howToGoogleAuthenticatorLink"
                                            data-automation-id="how_to_google_authenticator_link"
                                            onClick={() => showHowToGoogleAuthenticatorDialog && showHowToGoogleAuthenticatorDialog()}>{getLabelFromJspVariables('action_click_here')}</Link>
                                    </Box>
                                    <Box>
                                        <img alt="QR Code" src={'data:image/jpeg;base64,' + qrCodeImgSrc} />
                                    </Box>
                                    <Box style={{ marginLeft: '0.3rem', marginRight: '1rem', lineHeight: '1.3rem' }}>
                                        {getLabelFromJspVariables('note_scan_qr_enter_code')}
                                    </Box>
                                    <Box style={{ marginLeft: '-0.6rem', paddingBottom: '1rem' }}>
                                        <TextInput
                                            id="authenticator_otp_code"
                                            name="otpCode"
                                            label={getLabelFromJspVariables('label_otp_code')}
                                            required
                                            autoComplete="one-time-code"
                                            placeholder={getLabelFromJspVariables('label_enter_6_digit_code')}
                                            maxLength={6}
                                            numericOnly
                                            error={!!fieldErrors.otpCode}
                                            helperText={fieldErrors.otpCode}
                                            value={txtOtpCodeVerify}
                                            onChange={onOtpCodeChange}
                                        />
                                    </Box>
                                </Paper>
                            </Slide>
                        </Box>
                    }
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}
                    data-automation-id="tfa_setup_authenticator_close_btn">{getLabelFromJspVariables('action_close')}</Button>
                {step === STEP_DEVICE_NAME &&
                    <Button variant="contained" name="buttonRegistration"
                        onClick={startRegistration}
                        data-automation-id="button_registration">
                        {getLabelFromJspVariables('action_next')}
                    </Button>
                }
                {step === STEP_QR_CODE &&
                    <Button variant="contained" name="buttonRegistrationVerify"
                        onClick={verifyOtp}
                        data-automation-id="button_registration_verify">
                        {getLabelFromJspVariables('action_verify')}
                    </Button>
                }
                {step === STEP_SUCCESS && <>
                    <Button variant="outlined"
                        onClick={registerAnother}
                        data-automation-id="setup_authenticator_register_another_btn">
                        {getLabelFromJspVariables('action_register_another')}
                    </Button>
                    {onSetAsDefault &&
                        <Button variant="contained"
                            onClick={onSetAsDefault}
                            data-automation-id="setup_authenticator_set_default_btn">
                            {getLabelFromJspVariables('action_set_authenticator_default')}
                        </Button>
                    }
                </>}
            </DialogActions>
        </MuiDialog>
    );
};

SetupAuthenticator.displayName = 'SetupAuthenticator';

export default SetupAuthenticator;
